import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { BmsBillingPeriod, BmsMonthOfYear } from '@core/models/bms-export-bot';
import { GetExportInfoResponse, GrpcTrusteeExportInfo } from '@core/xprojector_backend/proto/xprojector.modulebms.export.pb';
import { XProjectorBmsExportClient } from '@core/xprojector_backend/xprojector-bms-export-client';
import { NGXLogger } from 'ngx-logger';
import { DateHelper } from 'xproj-lib';
import * as XLSX from 'xlsx';
import { BmsAdminService } from '../../services/bms-admin-service';

@Component({
  selector: 'app-bms-trustee-overview',
  templateUrl: './bms-trustee-overview.component.html',
  styleUrls: ['./bms-trustee-overview.component.scss']
})
export class BmsTrusteeOverviewComponent implements OnInit {

  exportPeriods : { start: Date, end: Date }[] = [];
  exportPeriodsSpecial : { start: Date, end: Date }[] = [];
  selectedExportPeriod : number = 1;
  selectedBillingPeriod : BmsBillingPeriod = BmsBillingPeriod.Month;
  generatingExportReport : boolean = false;

  selectedExportPeriodSpecial : number = 1;
  selectedBillingPeriodSpecial : BmsBillingPeriod = BmsBillingPeriod.Month;

  exportInfo : {timestamp : Date, data : GetExportInfoResponse, start : Date, end : Date} | undefined;
  exportSpecialInfo : {timestamp : Date, data : GetExportInfoResponse, start : Date, end : Date} | undefined;
  filterSpecialAfter15 : boolean = false;
  filterExcludeAfter15 : boolean = false;
  filterShowOnlyNotInvoiced : boolean = false;
  filterShowOnlyNotInvoicedSpecial : boolean = false;
  filterShowInvoicedActive : boolean = true;
  filterShowInvoicedActiveSpecial : boolean = true;
  filterShowOnlyNotSigned : boolean = false;
  filterShowOnlyNotSignedSpecial : boolean = false;

  exportStatusShowDetailed : boolean = true;

  reportActive : boolean = true;
  _specialActive : boolean = false;
  get specialActive() : boolean {
    return this._specialActive;
  }
  set specialActive(value: boolean) {
    this._specialActive = value;
    if (this._specialActive && !this.exportSpecialInfo) {
      this.getCachedSpecialReport();
    }
  }
  trusteeCustomerActive : boolean = false;

  editTrusteeInvoiceInfoText : string = '';
  editTrusteeInvoiceInfo : GrpcTrusteeExportInfo;
  editTrusteeScheduleText : string = '';
  editTrusteeScheduleInfo : GrpcTrusteeExportInfo;
  editTrusteeUserText : string = '';
  editTrusteeUserInfo : GrpcTrusteeExportInfo;
  editTrusteeReportDayNumber : number = 0;
  editTrusteeReportDayInfo : GrpcTrusteeExportInfo;

  BmsBillingPeriod = BmsBillingPeriod;

  constructor(
    private logger: NGXLogger,
    private bmsExportClient : XProjectorBmsExportClient,
    private adminService: BmsAdminService,
    private dateHelper: DateHelper,
    private cdr: ChangeDetectorRef,
  ) { }

  async ngOnInit() {
    this.exportPeriods = this.getExportPeriods(BmsBillingPeriod.Month, BmsMonthOfYear.March);
    this.exportPeriodsSpecial = this.getExportPeriods(BmsBillingPeriod.Month, BmsMonthOfYear.March);
    this.selectedExportPeriod = Number.parseInt(localStorage.getItem("xprojector-bmstrustee-selectedexportperiod") || "1");
    this.selectedBillingPeriod = BmsBillingPeriod[localStorage.getItem("xprojector-bmstrustee-selectedbillingperiod") || "Month"];

    this.getCachedReport();

    this.selectedExportPeriodSpecial = Number.parseInt(localStorage.getItem("xprojector-bmstrustee-selectedexportperiod-special") || "1");
    this.selectedBillingPeriodSpecial = BmsBillingPeriod[localStorage.getItem("xprojector-bmstrustee-selectedbillingperiod-special") || "Month"];
  }

  formatByString(date: Date, format: string, displayUTC: boolean = false): string {
    let d = displayUTC ? this.dateHelper.utils.addMinutes(date, date.getTimezoneOffset()) : date;
    return this.dateHelper.utils.formatByString(d, format);
  }

  getExportPeriods(billingPeriod: BmsBillingPeriod, startMonth: BmsMonthOfYear): { start: Date, end: Date }[] {
    let result : { start: Date, end: Date }[] = [];
    let now = new Date();

    switch (billingPeriod ?? BmsBillingPeriod.Month) {
      case BmsBillingPeriod.Week:
        let startOfWeek = this.dateHelper.utils.startOfWeek(now);
        let endOfWeek = this.dateHelper.utils.endOfWeek(now);
        for (let i = 0; i < 13; i++) {
          result.push({ start: this.dateHelper.utils.addWeeks(startOfWeek, -i), end: this.dateHelper.utils.addWeeks(endOfWeek, -i) });
        }
        break;

      case BmsBillingPeriod.Month:
        for (let i = 0; i < 13; i++) {
          let month = this.dateHelper.utils.addMonths(now, -i);
          let startOfMonth = this.dateHelper.utils.startOfMonth(month);
          let endOfMonth = this.dateHelper.utils.endOfMonth(month);
          result.push({ start: startOfMonth, end: endOfMonth });
        }
        break;

      case BmsBillingPeriod.Quarter:
        let month = this.dateHelper.utils.getMonth(now);
        let offset = 0 + ((month + 4 - (1 + ((startMonth - 1) % 3))) % 3);

        for (let i = 0; i < 13; i++) {
          let monthStart = this.dateHelper.utils.addMonths(now, -((i * 3) + offset));
          let monthEnd = this.dateHelper.utils.addMonths(now, -((i * 3) + offset) + 2);
          let startOfMonth = this.dateHelper.utils.startOfMonth(monthStart);
          let endOfMonth = this.dateHelper.utils.endOfMonth(monthEnd);
          result.push({ start: startOfMonth, end: endOfMonth });
        }
        break;

      case BmsBillingPeriod.SixMonths:
        let monthSixMonths = this.dateHelper.utils.getMonth(now);
        let offsetSixMonths = 0 + ((monthSixMonths + 7 - (1 + ((startMonth - 1) % 6))) % 6);

        for (let i = 0; i < 7; i++) {
          let monthStart = this.dateHelper.utils.addMonths(now, -((i * 6) + offsetSixMonths));
          let monthEnd = this.dateHelper.utils.addMonths(now, -((i * 6) + offsetSixMonths) + 5);
          let startOfMonth = this.dateHelper.utils.startOfMonth(monthStart);
          let endOfMonth = this.dateHelper.utils.endOfMonth(monthEnd);
          result.push({ start: startOfMonth, end: endOfMonth });
        }
        break;

      default:
        for (let i = 1; i < 13; i++) {
          let month = this.dateHelper.utils.addMonths(now, -i);
          let startOfMonth = this.dateHelper.utils.startOfMonth(month);
          let endOfMonth = this.dateHelper.utils.endOfMonth(month);
          result.push({ start: startOfMonth, end: endOfMonth });
        }
        break;
    }

    return result;
  }

  selectedBillingPeriodChange() {
    this.exportPeriods = this.getExportPeriods(this.selectedBillingPeriod, BmsMonthOfYear.March);
    this.selectedExportPeriod = 1;
  }

  selectedExportPeriodChange() {
    this.getCachedReport();
  }

  selectedBillingPeriodSpecialChange() {
    this.exportPeriodsSpecial = this.getExportPeriods(this.selectedBillingPeriodSpecial, BmsMonthOfYear.March);
    this.selectedExportPeriodSpecial = 1;
  }

  selectedExportPeriodSpecialChange() {
    this.getCachedSpecialReport();
  }


  async getCachedReport() {
    try {
      this.generatingExportReport = true;
      this.exportInfo = undefined;
      this.cdr.detectChanges();
      let result = await this.bmsExportClient.getExportInfo(this.selectedBillingPeriod,
          this.dateHelper.utils.addMinutes(this.exportPeriods[this.selectedExportPeriod].start, -this.exportPeriods[this.selectedExportPeriod].start.getTimezoneOffset()),
          this.dateHelper.utils.addMinutes(this.exportPeriods[this.selectedExportPeriod].end, -this.exportPeriods[this.selectedExportPeriod].end.getTimezoneOffset()),
          1, false, false, false, 0, '', true
      );

      this.exportInfo = {timestamp : result.timestamp.toDate(), data : result, start : result.start.toDate(), end : result.end.toDate() };

      this.logger.info('exportInfo', this.exportInfo);
    }
    finally {
      this.generatingExportReport = false;
    }
  }

  async getCachedSpecialReport() {
    try {
      this.generatingExportReport = true;
      this.exportSpecialInfo = undefined;
      this.cdr.detectChanges();
      let result = await this.bmsExportClient.getExportInfo(this.selectedBillingPeriodSpecial,
        this.dateHelper.utils.addMinutes(this.exportPeriodsSpecial[this.selectedExportPeriodSpecial].start, -this.exportPeriodsSpecial[this.selectedExportPeriodSpecial].start.getTimezoneOffset()),
        this.dateHelper.utils.addMinutes(this.exportPeriodsSpecial[this.selectedExportPeriodSpecial].end, -this.exportPeriodsSpecial[this.selectedExportPeriodSpecial].end.getTimezoneOffset()),
        2, true, true, false, 0, '', true
      );

      this.exportSpecialInfo = {timestamp : result.timestamp.toDate(), data : result, start : result.start.toDate(), end : result.end.toDate() };

      this.logger.info('exportSpecialInfo', this.exportSpecialInfo);
    }
    finally {
      this.generatingExportReport = false;
    }
  }

  async onViewReport() {
    try {
      this.generatingExportReport = true;
      this.exportInfo = undefined;
      this.cdr.detectChanges();
      let result = await this.bmsExportClient.getExportInfo(this.selectedBillingPeriod,
          this.dateHelper.utils.addMinutes(this.exportPeriods[this.selectedExportPeriod].start, -this.exportPeriods[this.selectedExportPeriod].start.getTimezoneOffset()),
          this.dateHelper.utils.addMinutes(this.exportPeriods[this.selectedExportPeriod].end, -this.exportPeriods[this.selectedExportPeriod].end.getTimezoneOffset()),
          1, false, false, false
      );

      this.exportInfo = {timestamp : result.timestamp.toDate(), data : result, start : result.start.toDate(), end : result.end.toDate() };

      localStorage.setItem("xprojector-bmstrustee-selectedexportperiod", this.selectedExportPeriod.toString());
      localStorage.setItem("xprojector-bmstrustee-selectedbillingperiod", BmsBillingPeriod[this.selectedBillingPeriod]);
      this.logger.info('exportInfo', this.exportInfo);
    }
    finally {
      this.generatingExportReport = false;
    }
  }

  async onViewSpecialReport() {
    try {
      this.generatingExportReport = true;
      this.exportSpecialInfo = undefined;
      this.cdr.detectChanges();
      let result = await this.bmsExportClient.getExportInfo(this.selectedBillingPeriodSpecial,
          this.dateHelper.utils.addMinutes(this.exportPeriodsSpecial[this.selectedExportPeriodSpecial].start, -this.exportPeriodsSpecial[this.selectedExportPeriodSpecial].start.getTimezoneOffset()),
          this.dateHelper.utils.addMinutes(this.exportPeriodsSpecial[this.selectedExportPeriodSpecial].end, -this.exportPeriodsSpecial[this.selectedExportPeriodSpecial].end.getTimezoneOffset()),
          2, true, true, false
      );

      this.exportSpecialInfo = {timestamp : result.timestamp.toDate(), data : result, start : result.start.toDate(), end : result.end.toDate() };

      localStorage.setItem("xprojector-bmstrustee-selectedexportperiod-special", this.selectedExportPeriodSpecial.toString());
      localStorage.setItem("xprojector-bmstrustee-selectedbillingperiod-special", BmsBillingPeriod[this.selectedBillingPeriodSpecial]);

      this.logger.info('exportSpecialInfo', this.exportSpecialInfo);
    }
    finally {
      this.generatingExportReport = false;
    }
  }

  exportReport(onlyNotInvoiced : boolean = false) {
    if (this.exportInfo) {
        let rows : any[] = [];
        var headers: string[] = ['Trustee', 'Customer', 'AssociationId', 'ContractType', 'InvoiceActive', 'Signed', 'Invoiced', 'After15',  'TariffsSet', 'InvoiceInfo'];

        this.exportInfo.data.trustees.forEach(trustee => {
          trustee.customers.forEach(customer => {
            if (!onlyNotInvoiced || !customer.invoiced) {
              rows.push({
                Trustee : trustee.name,
                Customer : customer.name,
                AssociationId : customer.associationId,
                ContractType : customer.contractType,
                InvoiceActive : customer.billingEnabled ? 'yes' : 'no',
                Signed : customer.signed ? 'yes' : 'no',
                Invoiced : customer.invoiced ? 'yes' : 'no',
                After15 : (customer.invoiceDay >= 2) ? 'yes' : 'no',
                TariffsSet : customer.tariffsSet ? 'yes' : 'no',
                InvoiceInfo : customer.invoiceInfo
              });
            }
          });
        });

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows, { header: headers });

        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ExportInfo');
        try {
          XLSX.writeFile(wb, 'InvoiceReport_' + this.formatByString(this.exportInfo.start, 'yyyyMMdd') + '_' +
          this.formatByString(this.exportInfo.end, 'yyyyMMdd') + '.xlsx');
        }
        catch {
          XLSX.writeFile(wb, 'InvoiceReport.xlsx');
        }
    }
  }

  exportSpecialReport() {
    if (this.exportSpecialInfo) {
        let rows : any[] = [];
        var headers: string[] = ['Trustee', 'Customer', 'ContractType', 'InvoiceActive', 'Signed', 'Invoiced', 'After15', 'TariffsSet', 'InvoiceInfo'];

        this.exportSpecialInfo.data.trustees.forEach(trustee => {
          trustee.customers.forEach(customer => {
            rows.push({
              Trustee : trustee.name,
              Customer : customer.name,
              ContractType : customer.contractType,
              InvoiceActive : customer.billingEnabled ? 'yes' : 'no',
              Signed : customer.signed ? 'yes' : 'no',
              Invoiced : customer.invoiced ? 'yes' : 'no',
              After15 : (customer.invoiceDay >= 2) ? 'yes' : 'no',
              TariffsSet : customer.tariffsSet ? 'yes' : 'no',
              InvoiceInfo : customer.invoiceInfo });
          });
        });

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows, { header: headers });

        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ExportInfo');
        try {
          XLSX.writeFile(wb, 'ExportSpecialReport_' + this.formatByString(this.exportSpecialInfo.start, 'yyyyMMdd') + '_' +
          this.formatByString(this.exportSpecialInfo.end, 'yyyyMMdd') + '.xlsx');
        }
        catch {
          XLSX.writeFile(wb, 'ExportSpecialReport.xlsx');
        }
    }
  }

   exportErrorsReport(onlyCustomersWithError : boolean = true) {
    if (this.exportInfo) {
        let rows : any[] = [];
        var headers: string[] = ['Trustee', 'Customer', 'Errors', 'MissingStart', 'MissingEnd', 'ZeroConsumption',
              'LowConsumption', 'HighConsumption', 'ExHighConsumption', 'NefativeConsumption', 'MissingTariff', 'MissingPrevEnd', 'HasComment'];

        this.exportInfo.data.trustees.forEach(trustee => {
          trustee.customers.forEach(customer => {
            if (!onlyCustomersWithError || customer.meterErrorCount > 0) {
              rows.push({
                Trustee : trustee.name,
                Customer : customer.name,
                Errors : customer.meterErrorCount,
                MissingStart : customer.meterMissingStartValueCount,
                MissingEnd : customer.meterMissingEndValueCount,
                ZeroConsumption : customer.meterZeroConsumptionCount,
                LowConsumption : customer.meterLowConsumptionCount,
                HighConsumption : customer.meterHighConsumptionCount,
                ExHighConsumption : customer.meterExceedinglyHighCount,
                NefativeConsumption : customer.meterNegativeConsumptionCount,
                MissingTariff : customer.meterMissingTariffCount,
                MissingPrevEnd : customer.meterMissingPrevEndCount,
                HasComment : customer.meterHasCommentCount
              });
            }
          });
        });

        rows.push({
          Trustee : 'Total',
          Customer : 'Total',
          Errors : this.exportInfo.data.meterErrorCount,
          MissingStart : this.exportInfo.data.meterMissingStartValueCount,
          MissingEnd : this.exportInfo.data.meterMissingEndValueCount,
          ZeroConsumption : this.exportInfo.data.meterZeroConsumptionCount,
          LowConsumption : this.exportInfo.data.meterLowConsumptionCount,
          HighConsumption : this.exportInfo.data.meterHighConsumptionCount,
          ExHighConsumption : this.exportInfo.data.meterExceedinglyHighCount,
          NefativeConsumption : this.exportInfo.data.meterNegativeConsumptionCount,
          MissingTariff : this.exportInfo.data.meterMissingTariffCount,
          MissingPrevEnd : this.exportInfo.data.meterMissingPrevEndCount,
          HasComment : this.exportInfo.data.meterHasCommentCount
        });

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows, { header: headers });

        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ExportErrorInfo');
        try {
          XLSX.writeFile(wb, 'ErrorReport_' + this.formatByString(this.exportInfo.start, 'yyyyMMdd') + '_' +
          this.formatByString(this.exportInfo.end, 'yyyyMMdd') + '.xlsx');
        }
        catch {
          XLSX.writeFile(wb, 'ErrorReport.xlsx');
        }
    }
  }

  editTrusteeSchedule(trusteeInfo : GrpcTrusteeExportInfo) {
    this.editTrusteeScheduleInfo = trusteeInfo;
    this.editTrusteeUserInfo = null;
    this.editTrusteeReportDayInfo = null;
    this.editTrusteeScheduleText = trusteeInfo.invoiceSchedule;
  }

  async saveTrusteeSchedule() {
    if (this.editTrusteeScheduleInfo) {
      let response = await this.adminService.saveTrusteeInvoiceInfo(this.editTrusteeScheduleInfo.customerId, this.editTrusteeScheduleText, undefined);
      if (response.result) {
        this.editTrusteeScheduleInfo.invoiceSchedule = this.editTrusteeScheduleText
        this.editTrusteeScheduleText = '';
      }
      this.editTrusteeScheduleInfo = null;
    }
  }

  cancelEditTrusteeInfo() {
    this.editTrusteeInvoiceInfo = null;
  }

  editTrusteeInfo(trusteeInfo : GrpcTrusteeExportInfo) {
    this.editTrusteeInvoiceInfo = trusteeInfo;
    this.editTrusteeScheduleInfo = null;
    this.editTrusteeUserInfo = null;
    this.editTrusteeReportDayInfo = null;
    this.editTrusteeInvoiceInfoText = trusteeInfo.invoiceInfo;
  }

  async saveTrusteeInfo() {
    if (this.editTrusteeInvoiceInfo) {
      let response = await this.adminService.saveTrusteeInvoiceInfo(this.editTrusteeInvoiceInfo.customerId, undefined, undefined, undefined, undefined, this.editTrusteeInvoiceInfoText);
      if (response.result) {
        this.editTrusteeInvoiceInfo.invoiceInfo = this.editTrusteeInvoiceInfoText
        this.editTrusteeInvoiceInfoText = '';
      }
      this.editTrusteeInvoiceInfo = null;
    }
  }

  cancelEditTrusteeSchedule() {
    this.editTrusteeScheduleInfo = null;
  }

  editTrusteeUser(trusteeInfo : GrpcTrusteeExportInfo) {
    this.editTrusteeUserInfo = trusteeInfo;
    this.editTrusteeInvoiceInfo = null;
    this.editTrusteeScheduleInfo = null;
    this.editTrusteeReportDayInfo = null;
    this.editTrusteeUserText = trusteeInfo.invoiceUser;
  }

  async saveTrusteeUser() {
    if (this.editTrusteeUserInfo) {
      let response = await this.adminService.saveTrusteeInvoiceInfo(this.editTrusteeUserInfo.customerId, undefined, this.editTrusteeUserText);
      if (response.result) {
        this.editTrusteeUserInfo.invoiceUser = this.editTrusteeUserText
        this.editTrusteeUserText = '';
      }
      this.editTrusteeUserInfo = null;
    }
  }

  cancelEditTrusteeUser() {
    this.editTrusteeUserInfo = null;
  }

  editTrusteeReportDay(trusteeInfo : GrpcTrusteeExportInfo) {
    this.editTrusteeScheduleInfo = null;
    this.editTrusteeUserInfo = null;
    this.editTrusteeInvoiceInfo = null;
    this.editTrusteeReportDayInfo = trusteeInfo;
    this.editTrusteeReportDayNumber = trusteeInfo.invoiceReportDay;
  }

  async saveTrusteeReportDay() {
    if (this.editTrusteeReportDayInfo) {
      let response = await this.adminService.saveTrusteeInvoiceInfo(this.editTrusteeReportDayInfo.customerId, undefined, undefined, this.editTrusteeReportDayNumber, undefined);
      if (response.result) {
        this.editTrusteeReportDayInfo.invoiceReportDay = this.editTrusteeReportDayNumber;
        this.editTrusteeReportDayNumber = -1;
      }
      this.editTrusteeReportDayInfo = null;
    }
  }

  cancelEditTrusteeReportDay() {
    this.editTrusteeReportDayInfo = null;
  }

  async saveTrusteeReportEnabled(truestteInfo : GrpcTrusteeExportInfo) {
    if (truestteInfo) {
      let response = await this.adminService.saveTrusteeInvoiceInfo(truestteInfo.customerId, undefined, undefined, undefined, truestteInfo.invoiceReportEnabled);
      if (response.result) {

      }
    }
  }


}
